// frontend/src/components/AIOperationsHub.jsx
// Version: 2.0 (quick-260915-d2d — выдуманная клиентская фактура убрана, журнал звонков живой)
// Description: Единый операционный командный центр для всех AI-агентов (Katifi, Sofia Voice,
//              Elena Paralegal, AI Council): плеер записей звонков, интерактивный транскрипт,
//              генерация attorney notes, консилиум по рискам и live-сигналы.
//
// 🔴 ЧТО УБРАНО 15.09.2026 И ПОЧЕМУ ЭТО НЕ КОСМЕТИКА. На боевом экране
//    юридической фирмы стояли ВЫДУМАННЫЕ клиентские записи: два звонка с
//    именами, телефонами, стенограммами и деталями иммиграционных дел; очередь
//    paralegal с фамилиями и номерами форм; памятка о сроке I-94 конкретного
//    человека; решение консилиума с оценкой риска 90/Critical по несуществующему
//    делу. Пометки «демо» не было НИ ОДНОЙ — отличить выдуманную запись от
//    настоящей было нечем ни оператору, ни владельцу.
//
// 🔴 ПОЧЕМУ УДАЛЕНО ЦЕЛИКОМ, А НЕ СПРЯТАНО ПОД ФЛАГ. Константа, которую можно
//    вернуть одной строкой, возвращается. Флаг, который ничего не переключает
//    в бою, — украшение: [[knowledge_decorative_flag]].
//
// 🔴 ЧЕМ ЗАМЕНЕНО. Живой журнал звонков: ручка GET /voice/calls/{profile_id}
//    (рубеж get_current_staff_user) через хук useCallHistory. У экрана ТРИ
//    состояния, а не два: «грузим», «отказ ручки — с причиной» и «записей нет».
//    Там, где живого источника НЕТ (очередь paralegal, решения консилиума),
//    экран говорит об этом ПРЯМО, а не показывает правдоподобную выдумку.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiService from '../services/apiService';
import useNavCounters from '../hooks/useNavCounters';
import { useCallHistory } from '../hooks/useVoiceAPI';
import { formatPhoneNumber, formatDate } from '../lib/utils';
import {
  Layers,
  Sparkles,
  Bot,
  PhoneCall,
  FileCheck2,
  Scale,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Clock,
  User,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  MapPin,
  CheckCircle2,
  X,
  FileText,
  Sliders,
  Radio,
  ExternalLink,
  ChevronRight,
  Briefcase
} from 'lucide-react';

// Список постоянных AI-агентов практики Fitenko Law
const AI_AGENTS = [
  {
    name: 'Katifi',
    role: 'Command center',
    status: 'Working',
    tone: 'bg-[#20231F] text-white',
    icon: Sparkles,
    detail: 'Monitors active cases, deadlines and incoming requests across the firm.'
  },
  {
    name: 'Sofia Voice',
    role: 'Front desk & calls',
    status: 'Ready',
    tone: 'bg-[#FEF4E6] text-[#D78416]',
    icon: PhoneCall,
    detail: 'Answers calls 24/7, captures intake facts and schedules consultations.'
  },
  {
    name: 'Elena Paralegal',
    role: 'Evidence & forms',
    status: 'Reviewing',
    tone: 'bg-[#EBF2FD] text-[#3674D8]',
    icon: FileCheck2,
    detail: 'Checks documents against USCIS checklists, identifies gaps and prepares drafts.'
  },
  {
    name: 'AI Council',
    role: 'Risk & legal strategy',
    status: '1 decision',
    tone: 'bg-[#F2EEFD] text-[#7554D8]',
    icon: Scale,
    detail: 'Surfaces legal disagreements between team roles and creates decision-ready memos.'
  }
];

// Отбор журнала звонков. Объявлен ВНЕ компонента нарочно: новый объект на каждом
// проходе отрисовки перезапускал бы запрос по кругу.
const OTBOR_ZVONKOV = {};

const formatDuration = (sec) => {
  const s = Number(sec);
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const celye = Math.floor(s);
  return `${Math.floor(celye / 60)}:${String(celye % 60).padStart(2, '0')}`;
};

// Подпись звонка в списке. Имени звонившего ручка НЕ несёт — и выдумывать его
// нельзя: показываем ровно то, что канал гарантирует, — номер.
const podpisZvonka = (call) => formatPhoneNumber(call?.caller_number);

// Реплики расшифровки: держатель формы один — `transcript_json.messages`,
// ровно как читает `components/voice/CallHistoryTable.js:413`.
const replikiZvonka = (call) => {
  const soobshcheniya = call?.transcript_json?.messages;
  return Array.isArray(soobshcheniya) ? soobshcheniya : [];
};

export default function AIOperationsHub({ onAskKatifi, onGoToCase }) {
  const navigate = useNavigate();

  const navCounters = useNavCounters();
  const [liveAgents, setLiveAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    apiService.getAgents()
      .then((data) => {
        if (active && Array.isArray(data) && data.length > 0) {
          setLiveAgents(data);
        }
      })
      .catch((err) => {
        console.error('Failed to load agents in AIOperationsHub:', err);
      })
      .finally(() => {
        if (active) setAgentsLoading(false);
      });
    return () => { active = false; };
  }, []);

  const [searchParams] = useSearchParams();
  const caseIdParam = searchParams.get('case');
  const caseTitleParam = searchParams.get('title');
  const caseClientParam = searchParams.get('client');
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(
    tabParam || (caseIdParam ? 'council' : 'team')
  );

  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    } else if (caseIdParam) {
      setActiveTab('council');
    }
  }, [tabParam, caseIdParam]);

  // 🔴 ЖИВОЙ ЖУРНАЛ ЗВОНКОВ. Три исхода приходят ТРЕМЯ разными значениями:
  //    zvonkiLoading — ещё не спросили; zvonkiError — спросили и получили отказ;
  //    пустой calls при обоих false — спросили, звонков нет. Раньше второй и
  //    третий приходили одинаковым пустым массивом.
  const {
    calls: zvonki,
    loading: zvonkiLoading,
    error: zvonkiError
  } = useCallHistory('default', OTBOR_ZVONKOV);

  const [selectedCallId, setSelectedCallId] = useState(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedLines, setSelectedLines] = useState([]);
  const [generatedNote, setGeneratedNote] = useState(null);
  const [showCouncilModal, setShowCouncilModal] = useState(false);
  const [showGuardrailsModal, setShowGuardrailsModal] = useState(false);

  // Выбранный звонок. Никакого «или первый попавшийся, если не нашли»: пока
  // выбора нет — его НЕТ, и экран об этом говорит.
  const activeCall = useMemo(
    () => (zvonki || []).find((r) => r.id === selectedCallId) || null,
    [zvonki, selectedCallId]
  );

  // Пришёл журнал — выбираем первый звонок. Если журнал пуст или отказал,
  // выбор остаётся пустым.
  useEffect(() => {
    if (!selectedCallId && Array.isArray(zvonki) && zvonki.length > 0) {
      setSelectedCallId(zvonki[0].id);
    }
  }, [zvonki, selectedCallId]);

  // Синхронизация audio при смене выбранного звонка
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load();
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(activeCall?.duration_seconds || 0);
    setSelectedLines([]);
    setGeneratedNote(null);
  }, [selectedCallId, activeCall]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.warn('Playback error:', err);
          setIsPlaying(false);
        });
    }
  };

  const seekTo = (seconds, autoPlay = false) => {
    const targetDuration = duration || activeCall?.duration_seconds || 0;
    const clamped = Math.max(0, Math.min(seconds, targetDuration));
    if (audioRef.current) {
      audioRef.current.currentTime = clamped;
      if (autoPlay && !isPlaying) {
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    }
    setCurrentTime(clamped);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Переключение записи звонка
  const handleSelectCall = (id) => {
    setSelectedCallId(id);
  };

  // Переключение выбора реплики в расшифровке. Опознаватель — ПОРЯДКОВЫЙ НОМЕР
  // реплики: метки времени у живых реплик нет, а текст повторяется («Yes»).
  const toggleLine = (nomerRepliki) => {
    setSelectedLines((prev) =>
      prev.includes(nomerRepliki)
        ? prev.filter((t) => t !== nomerRepliki)
        : [...prev, nomerRepliki]
    );
    setGeneratedNote(null);
  };

  // Черновик заметки адвоката из выбранных реплик.
  //
  // 🔴 НИ ОДНОГО ВЫДУМАННОГО СЛОВА. Прежняя сборка подставляла готовые «summary»
  //    и «Recommended next step» по признаку `id === 'sofia-parole'` — то есть
  //    юридический вывод НЕ вытекал из разговора, а был написан заранее. В
  //    черновике остаются ТОЛЬКО выбранные реплики: их сказал звонивший, их
  //    можно проверить по записи.
  const handleGenerateNote = () => {
    const repliki = replikiZvonka(activeCall);
    const lines = repliki
      .map((m, i) => [i, m])
      .filter(([i]) => selectedLines.includes(i));
    if (!lines.length) return;

    setGeneratedNote({
      zvonok: podpisZvonka(activeCall),
      kogda: formatDate(activeCall?.created_at),
      facts: lines.map(([, m]) =>
        `${m.role === 'assistant' ? 'Sofia (AI)' : 'Caller'}: ${m.content}`
      )
    });
    // 🔴 РАНЬШЕ ЗДЕСЬ СТОЯЛО УТВЕРЖДЕНИЕ О СОХРАНЕНИИ В ИСТОРИЮ — и это была НЕПРАВДА:
    //    ручки сохранения нет, черновик живёт в состоянии вкладки и умирает при
    //    переключении звонка. Обещание, которого никто не исполняет, хуже
    //    молчания: по нему человек перестаёт записывать сам.
    toast('Черновик составлен в браузере. В историю клиента он НЕ сохранён.', {
      icon: '📝'
    });
  };

  const handleKatifiQuery = (prompt) => {
    if (onAskKatifi) {
      onAskKatifi(prompt);
    } else {
      toast(`Katifi: "${prompt}" обрабатывается в контексте практики...`, {
        icon: '✨'
      });
    }
  };

  const handleNavigateCase = () => {
    if (onGoToCase) {
      onGoToCase();
    } else {
      navigate('/cases');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6 text-[#20231F]">
      {/* 1. Заголовок и вызов Katifi */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide font-bold text-[#7554D8]">
            <Layers className="w-3.5 h-3.5" />
            One command layer for every AI capability
          </div>
          <h1 className="text-3xl font-serif font-normal text-[#20231F] mt-1">
            AI Operations Hub
          </h1>
          <p className="text-sm text-[#687077] mt-1">
            Use one team view instead of separate AI dashboards. Katifi keeps every agent in the same client and case context.
          </p>
        </div>

        <button
          onClick={() =>
            handleKatifiQuery('Summarize what the AI team needs me to decide today')
          }
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#20231F] text-white text-xs font-bold hover:bg-black transition-colors cursor-pointer shadow-sm"
        >
          <Sparkles className="w-3.5 h-3.5 text-[#C8A653]" />
          Ask Katifi for priorities
        </button>
      </div>

      {/* 2. Три ключевые операционные метрики */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E1D6] rounded-xl p-4 shadow-2xs">
          <div className="text-[11px] uppercase tracking-wide font-bold text-[#687077]">
            Needs your decision
          </div>
          <div className="mt-2 flex items-end justify-between">
            {/* 🔴 Запасное число УБРАНО. `?? 3` подставлял выдуманную тройку,
                когда счётчик не ответил, — отказ измерителя снова становился
                неотличим от значения. Не ответил — так и написано. */}
            <span className="text-3xl font-serif text-[#20231F]">
              {navCounters?.inbox?.value ?? '—'}
            </span>
            <span className="text-[11px] font-bold text-[#687077]">
              {navCounters?.inbox?.value === undefined ? 'счётчик не ответил' : 'в разборе'}
            </span>
          </div>
        </div>

        <div className="bg-white border border-[#E5E1D6] rounded-xl p-4 shadow-2xs">
          <div className="text-[11px] uppercase tracking-wide font-bold text-[#687077]">
            AI Swarm Roster
          </div>
          <div className="mt-2 flex items-end justify-between">
            <span className="text-3xl font-serif text-[#20231F]">
              {liveAgents.length > 0 ? liveAgents.length : (agentsLoading ? '…' : '—')}
            </span>
            <span className="text-[11px] font-bold text-[#0B8B5E]">
              {liveAgents.length > 0
                ? `${liveAgents.filter((a) => a.is_active !== false).length} operational`
                : (agentsLoading ? 'спрашиваем реестр' : 'реестр агентов не ответил')}
            </span>
          </div>
        </div>

        <div className="bg-white border border-[#E5E1D6] rounded-xl p-4 shadow-2xs">
          <div className="text-[11px] uppercase tracking-wide font-bold text-[#687077]">
            Active Cases
          </div>
          <div className="mt-2 flex items-end justify-between">
            <span className="text-3xl font-serif text-[#20231F]">
              {navCounters?.cases?.value ?? '—'}
            </span>
            <span className="text-[11px] font-bold text-[#687077]">
              {navCounters?.clients?.value
                ? `Across ${navCounters.clients.value} clients`
                : 'число клиентов не получено'}
            </span>
          </div>
        </div>
      </section>

      {/* 3. Основная рабочая область (Табы + Правый сайдбар сигналов) */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5">
        {/* Левая колонка с табами */}
        <div className="bg-white border border-[#E5E1D6] rounded-xl overflow-hidden shadow-2xs">
          {/* Переключатель табов */}
          <div className="px-5 pt-3 border-b border-[#E5E1D6] flex items-center gap-1 overflow-x-auto">
            {[
              ['team', `AI Team${liveAgents.length > 0 ? ` (${liveAgents.length})` : ''}`],
              ['voice', 'Voice AI'],
              ['paralegal', 'Paralegal'],
              ['council', 'Council']
            ].map(([tabKey, tabLabel]) => (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-3 py-3 text-xs font-bold border-b-2 whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === tabKey
                    ? 'border-[#C8A653] text-[#20231F]'
                    : 'border-transparent text-[#687077] hover:text-[#20231F]'
                }`}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          {/* Таб 1: AI Team */}
          {activeTab === 'team' && (
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {(liveAgents.length > 0 ? liveAgents : AI_AGENTS).map((agent) => {
                const IconComponent = agent.icon || Bot;
                const status = agent.is_active !== false ? 'Active' : 'Standby';
                const xp = agent.compute_score ?? (agent.name === 'Katifi' ? 1540 : 1200);
                const role = agent.specialized_role || agent.role || 'Legal AI Specialist';
                const detail = agent.persona || agent.detail || 'Autonomous AI agent assisting the legal practice.';

                return (
                  <div
                    key={agent.id || agent.name}
                    className="p-4 border border-[#E5E1D6] rounded-xl hover:border-[#C8A653]/60 transition-colors bg-white flex flex-col justify-between shadow-2xs"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {agent.avatar_url ? (
                            <img
                              src={agent.avatar_url}
                              alt={agent.name}
                              className="w-10 h-10 rounded-lg object-cover border border-[#E5E1D6] shadow-2xs shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 ${
                                agent.tone || 'bg-[#20231F] text-[#C8A653]'
                              }`}
                            >
                              <IconComponent className="w-5 h-5" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <h2 className="text-sm font-bold text-[#20231F] truncate">
                                {agent.name}
                              </h2>
                              {agent.is_verified && (
                                <ShieldCheck className="w-3.5 h-3.5 text-[#3674D8] shrink-0" title="Verified Agent" />
                              )}
                            </div>
                            <p className="text-[11px] font-semibold text-[#687077] truncate">
                              {role}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {/* XP Score Badge */}
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FAF4E5] text-[#A97F2E] border border-[#C8A653]/40 flex items-center gap-1 shadow-2xs">
                            ⚡ XP: {xp}
                          </span>
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                              status === 'Active'
                                ? 'bg-[#E6F6EE] text-[#0B8B5E]'
                                : 'bg-[#FAF4E5] text-[#A97F2E]'
                            }`}
                          >
                            {status}
                          </span>
                        </div>
                      </div>

                      <p className="text-xs text-[#687077] leading-relaxed mt-3 line-clamp-3">
                        {detail}
                      </p>
                    </div>

                    <div className="mt-3.5 pt-2.5 border-t border-[#F1EEE4] flex items-center justify-between text-[10px] text-[#687077]">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-[#A97F2E]" />
                        {agent.current_location || 'Miami Headquarters'}
                      </span>
                      <span className="font-mono text-[9px] bg-[#F8F7F2] border border-[#E5E1D6] px-1.5 py-0.5 rounded text-[#20231F]">
                        {agent.llm_model || 'gpt-4o'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Таб 2: Voice AI */}
          {activeTab === 'voice' && (
            <div className="p-5 space-y-4">
              {/* Статус-баннер Софии */}
              <div className="flex items-center gap-3 p-4 rounded-xl bg-[#FEF4E6] border border-[#D78416]/20">
                <PhoneCall className="w-5 h-5 text-[#D78416] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#20231F]">
                    Sofia is ready for incoming calls
                  </p>
                  <p className="text-[11px] text-[#687077] mt-0.5 leading-relaxed">
                    English, Russian and Spanish are active. Recordings and transcripts are retained in the linked intake history.
                  </p>
                </div>
                <span className="text-[10px] font-bold bg-white px-2 py-1 rounded text-[#0B8B5E] shrink-0">
                  Online
                </span>
              </div>

              {/* Двухколоночный интерфейс: Список звонков + Аудиоплеер и Транскрипт */}
              <div className="grid grid-cols-1 lg:grid-cols-[210px_minmax(0,1fr)] gap-4">
                {/* Список звонков */}
                <aside className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold text-[#20231F]">
                      Recent recordings
                    </h3>
                    <span className="text-[10px] text-[#687077]">
                      {zvonkiLoading || zvonkiError ? '' : `${zvonki.length} в журнале`}
                    </span>
                  </div>

                  {/* 🔴 ТРИ ВИДА, А НЕ ДВА. «Грузим», «отказ ручки — с причиной» и
                      «записей нет» — три разных ответа, и одинаковыми словами они
                      не говорят. Раньше второй и третий выглядели одинаково. */}
                  {zvonkiLoading && (
                    <p className="text-[11px] text-[#687077] italic p-3 border border-dashed border-[#E5E1D6] rounded-xl">
                      Загружается…
                    </p>
                  )}

                  {!zvonkiLoading && zvonkiError && (
                    <p className="text-[11px] text-[#C13E38] p-3 border border-[#C84842]/40 bg-[#FDF2F2] rounded-xl leading-relaxed">
                      Не удалось получить журнал звонков: {zvonkiError}
                    </p>
                  )}

                  {!zvonkiLoading && !zvonkiError && zvonki.length === 0 && (
                    <p className="text-[11px] text-[#687077] italic p-3 border border-dashed border-[#E5E1D6] rounded-xl">
                      Записей звонков пока нет
                    </p>
                  )}

                  {!zvonkiLoading && !zvonkiError && zvonki.map((call) => (
                    <button
                      key={call.id}
                      onClick={() => handleSelectCall(call.id)}
                      className={`w-full p-3 rounded-xl border text-left transition-colors cursor-pointer ${
                        activeCall?.id === call.id
                          ? 'border-[#C8A653] bg-[#FAF4E5]'
                          : 'border-[#E5E1D6] hover:bg-[#F8F7F2]'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-[11px] font-bold text-[#20231F] truncate">
                          {podpisZvonka(call)}
                        </span>
                        <span className="text-[10px] text-[#687077] shrink-0">
                          {formatDuration(call.duration_seconds)}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#687077] mt-1">
                        {formatDate(call.created_at)}
                      </div>
                      <div className="text-[10px] font-semibold text-[#D78416] mt-1">
                        {call.outcome || call.status || call.direction || 'без исхода'}
                      </div>
                    </button>
                  ))}
                </aside>

                {/* Плеер и детали звонка */}
                <div className="border border-[#E5E1D6] rounded-xl overflow-hidden flex flex-col justify-between">
                  {/* Пустое состояние — БЕЗ карточек-призраков, без плеера и без
                      расшифровки: показывать нечего, и показывается ничего. */}
                  {!activeCall ? (
                    <div className="p-8 text-center text-xs text-[#687077] leading-relaxed">
                      {zvonkiLoading
                        ? 'Загружается…'
                        : zvonkiError
                        ? `Не удалось получить журнал звонков: ${zvonkiError}`
                        : 'Записей звонков пока нет'}
                    </div>
                  ) : (
                  <>
                  <div className="p-4 border-b border-[#E5E1D6] flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-[#20231F]">
                        {podpisZvonka(activeCall)}
                      </h3>
                      <p className="text-[11px] text-[#687077] mt-0.5">
                        {formatDate(activeCall.created_at)}
                        {activeCall.direction ? ` · ${activeCall.direction}` : ''}
                        {activeCall.outcome ? ` · ${activeCall.outcome}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/history')}
                      className="text-[11px] font-bold text-[#C8A653] hover:underline inline-flex items-center gap-1 cursor-pointer"
                    >
                      Open complete history →
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                    {/* 🔴 Сводки по звонку ручка НЕ отдаёт (`summary` в схеме
                        `CallHistoryItem` нет нарочно). Прежний «Katifi summary»
                        брал текст из выдуманной записи. Показываем ровно те
                        признаки, которые ручка называет. */}
                    <p className="text-xs leading-relaxed text-[#20231F]">
                      <strong className="text-[#20231F]">Признаки звонка:</strong>{' '}
                      {[
                        activeCall.lead_intent && `намерение — ${activeCall.lead_intent}`,
                        activeCall.sentiment && `тон — ${activeCall.sentiment}`,
                        activeCall.ended_reason && `завершён — ${activeCall.ended_reason}`
                      ].filter(Boolean).join(' · ') || 'ручка не назвала ни одного'}
                    </p>

                    {/* Тёмный карточный плеер. Нет ссылки на запись — нет и плеера. */}
                    {activeCall.recording_url ? (
                    <div className="rounded-xl bg-[#20231F] p-4 text-white shadow-md">
                      {/* Скрытый нативный HTML5 audio элемент */}
                      <audio
                        ref={audioRef}
                        src={activeCall.recording_url}
                        preload="auto"
                        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                        onLoadedMetadata={(e) => {
                          if (e.currentTarget.duration && !isNaN(e.currentTarget.duration)) {
                            setDuration(Math.round(e.currentTarget.duration));
                          }
                        }}
                        onEnded={() => {
                          setIsPlaying(false);
                          setCurrentTime(0);
                        }}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      />

                      <div className="flex items-center gap-3">
                        <button
                          onClick={togglePlay}
                          className="w-9 h-9 rounded-full bg-[#C8A653] text-[#20231F] grid place-items-center hover:bg-[#B8933E] cursor-pointer shrink-0 transition-all shadow-sm active:scale-95"
                          aria-label={isPlaying ? 'Пауза' : 'Слушать запись'}
                          title={isPlaying ? 'Пауза' : 'Слушать запись'}
                        >
                          {isPlaying ? (
                            <Pause className="w-4 h-4 fill-current" />
                          ) : (
                            <Play className="w-4 h-4 fill-current ml-0.5" />
                          )}
                        </button>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between text-[10px] text-white/65 font-mono">
                            <span>{formatDuration(Math.floor(currentTime))}</span>
                            <span>{formatDuration(duration || activeCall.duration_seconds)}</span>
                          </div>
                          <input
                            aria-label="Recording progress"
                            type="range"
                            min="0"
                            max={duration || activeCall.duration_seconds || 0}
                            value={currentTime}
                            onChange={(e) => seekTo(Number(e.target.value))}
                            className="mt-1 w-full accent-[#C8A653] cursor-pointer"
                          />
                        </div>

                        <button
                          onClick={toggleMute}
                          className="text-white/65 hover:text-white transition-colors cursor-pointer shrink-0 p-1"
                          title={isMuted ? 'Включить звук' : 'Выключить звук'}
                          aria-label={isMuted ? 'Включить звук' : 'Выключить звук'}
                        >
                          {isMuted ? (
                            <VolumeX className="w-4 h-4 text-[#C84842]" />
                          ) : (
                            <Volume2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {/* Интерактивная волновая диаграмма (Waveform) */}
                      <div className="h-6 mt-3 flex items-center gap-0.5">
                        {Array.from({ length: 54 }, (_, i) => {
                          const barTime = Math.round((i / 54) * (duration || activeCall.duration_seconds || 0));
                          const isPassed = currentTime >= barTime;
                          return (
                            <button
                              key={i}
                              onClick={() => seekTo(barTime)}
                              className={`flex-1 rounded-xs transition-all cursor-pointer ${
                                isPassed ? 'bg-[#C8A653]' : 'bg-white/20 hover:bg-[#C8A653]/60'
                              }`}
                              style={{ height: `${20 + ((i * 17) % 45)}%` }}
                              aria-label={`Seek to ${barTime} seconds`}
                            />
                          );
                        })}
                      </div>
                    </div>
                    ) : (
                      <p className="text-[11px] text-[#687077] italic rounded-xl border border-dashed border-[#E5E1D6] p-3">
                        Аудиозаписи у этого звонка нет
                      </p>
                    )}
                  </div>

                  {/* Расшифровка с выбором реплик */}
                  <div className="border-t border-[#E5E1D6] max-h-60 overflow-y-auto">
                    <div className="p-3 flex flex-wrap items-center justify-between gap-2 sticky top-0 bg-white border-b border-[#F1EEE4] z-10">
                      <div>
                        <span className="text-xs font-bold text-[#20231F]">
                          Transcript
                        </span>
                        <span className="block text-[10px] text-[#687077]">
                          Отметьте реплики, которые войдут в черновик заметки
                        </span>
                      </div>
                      <button
                        onClick={handleGenerateNote}
                        disabled={!selectedLines.length}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#F2EEFD] text-[#7554D8] text-[10px] font-bold disabled:opacity-40 cursor-pointer transition-colors"
                      >
                        <FileText className="w-3 h-3" />
                        Generate notes{' '}
                        {selectedLines.length ? `(${selectedLines.length})` : ''}
                      </button>
                    </div>

                    <div className="p-3 space-y-1.5">
                      {replikiZvonka(activeCall).length === 0 ? (
                        <p className="text-[11px] text-[#687077] italic py-2">
                          Расшифровки у этого звонка нет
                        </p>
                      ) : replikiZvonka(activeCall).map((m, idx) => {
                        const isSelected = selectedLines.includes(idx);
                        const govoritSofia = m.role === 'assistant';

                        return (
                          <label
                            key={idx}
                            className={`grid grid-cols-[16px_84px_minmax(0,1fr)] gap-2 text-[11px] rounded-lg p-1.5 transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-[#F2EEFD] ring-1 ring-[#7554D8]/30'
                                : 'hover:bg-[#F8F7F2]'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleLine(idx)}
                              className="mt-0.5 accent-[#7554D8] cursor-pointer"
                              aria-label={`Выбрать реплику №${idx + 1}`}
                            />
                            <span
                              className={`font-bold ${
                                govoritSofia ? 'text-[#3674D8]' : 'text-[#20231F]'
                              }`}
                            >
                              {govoritSofia ? 'Sofia (AI)' : 'Caller'}
                            </span>
                            <span className="text-[#687077] leading-relaxed">
                              {m.content}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Черновик заметки адвоката — ТОЛЬКО выбранные реплики */}
                  {generatedNote && (
                    <div className="m-4 p-4 rounded-xl border border-[#7554D8]/25 bg-[#F7F4FF] space-y-3 animate-in fade-in">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-xs font-bold text-[#7554D8]">
                          <Sparkles className="w-4 h-4" />
                          Черновик заметки адвоката
                        </div>
                        {/* 🔴 Прежде здесь стояло «✓ Automatically saved to client
                            history» — утверждение о сохранении, которого не было. */}
                        <span className="text-[10px] font-bold text-[#A97F2E]">
                          Черновик в браузере · в историю НЕ сохранён
                        </span>
                      </div>

                      <div>
                        <div className="text-[10px] uppercase tracking-wide font-bold text-[#687077]">
                          Звонок
                        </div>
                        <p className="text-xs leading-relaxed text-[#20231F] mt-0.5">
                          {generatedNote.zvonok} · {generatedNote.kogda}
                        </p>
                      </div>

                      <div>
                        <div className="text-[10px] uppercase tracking-wide font-bold text-[#687077]">
                          Выбранные реплики
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {generatedNote.facts.map((f, i) => (
                            <li
                              key={i}
                              className="text-[11px] leading-relaxed text-[#687077]"
                            >
                              • {f}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <button
                          onClick={() => navigate('/history')}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#20231F] text-white text-[11px] font-bold hover:bg-black cursor-pointer transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5 text-[#C8A653]" />
                          Открыть историю
                        </button>
                        <button
                          onClick={() =>
                            handleKatifiQuery(
                              `Разбери реплики звонка ${generatedNote.zvonok} (${generatedNote.kogda}): ${generatedNote.facts.join(' | ')}`
                            )
                          }
                          className="text-[11px] font-bold text-[#7554D8] hover:underline cursor-pointer"
                        >
                          Open in Katifi for refinement →
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>
              </div>

              <button
                onClick={() => navigate('/inbox')}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C8A653] hover:underline cursor-pointer"
              >
                Review all calls in Omnichannel Inbox
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Таб 3: Paralegal Queue */}
          {/* 🔴 ИСТОЧНИКА У ЭТОЙ ОЧЕРЕДИ НЕТ. Здесь стояли ДВА выдуманных дела с
              фамилиями клиентов и номерами форм (E-2, I-130/I-485) — правдоподобные
              настолько, что отличить их от настоящих было нечем. Ручки «очередь
              paralegal» в бэкенде не существует: грепом по backend/app/api не
              нашлось ни одной. Выдумку убрали, источник не завели — и экран
              говорит об этом ПРЯМО, а не молчит и не показывает пустой список,
              который читался бы как «работы нет». */}
          {activeTab === 'paralegal' && (
            <div className="p-5">
              <div className="p-4 border border-dashed border-[#E5E1D6] rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <FileCheck2 className="w-5 h-5 text-[#687077] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-[#20231F]">
                      Очередь paralegal к источнику не подключена
                    </p>
                    <p className="text-xs text-[#687077] mt-1 leading-relaxed">
                      Ручки, которая отдаёт задачи проверки доказательств, в системе пока нет.
                      Пустое место здесь честнее правдоподобного списка: «работы нет» и
                      «спросить некого» — разные вещи. Дела смотрите в разделе дел.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleNavigateCase}
                  className="px-3.5 py-2 text-xs font-bold rounded-lg bg-[#EBF2FD] text-[#3674D8] hover:bg-[#DDEBFC] transition-colors cursor-pointer shrink-0 inline-flex items-center gap-1.5"
                >
                  Открыть дела
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Таб 4: Council & Decision Memos */}
          {activeTab === 'council' && (
            <div className="p-5 space-y-4">
              {caseIdParam && (
                <div className="p-3.5 rounded-xl bg-[#FAF4E5] border border-[#C8A653]/50 flex items-center justify-between gap-3 shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Briefcase className="w-4 h-4 text-[#83692A] shrink-0" />
                    <div className="text-xs text-[#20231F] truncate">
                      Активный контекст дела: <strong className="font-bold">Дело №{caseIdParam} • {caseTitleParam || 'Иммиграционное дело'}</strong>
                      {caseClientParam && <span className="text-[#687077]"> ({caseClientParam})</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/cases/${caseIdParam}`)}
                    className="text-xs text-[#83692A] hover:text-[#20231F] font-bold underline shrink-0 cursor-pointer inline-flex items-center gap-1"
                  >
                    ← Вернуться в дело
                  </button>
                </div>
              )}

              {/* 🔴 ВЫДУМАННОЕ РЕШЕНИЕ КОНСИЛИУМА УБРАНО. Без параметра дела здесь
                  стояла готовая правовая оценка по несуществующему клиенту: риск
                  продления пароля, срок накопления unlawful presence и выбор между
                  двумя путями подачи. Это не заглушка, а юридическое заключение,
                  выданное за работу системы. Ручки, которая отдаёт решения
                  консилиума, нет — и пока её нет, показывать нечего. */}
              <div className="rounded-xl bg-[#121912] p-5 text-white space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide font-bold text-[#C8A653]">
                      Decision-ready memo {caseIdParam ? `• Дело №${caseIdParam}` : ''}
                    </div>
                    <h2 className="text-base font-bold mt-1 text-white">
                      {caseIdParam
                        ? `${caseTitleParam || 'Иммиграционное дело'}${caseClientParam ? ` • ${caseClientParam}` : ''}`
                        : 'Дело не выбрано'}
                    </h2>
                  </div>
                  <Scale className="w-5 h-5 text-[#C8A653] shrink-0" />
                </div>

                <p className="text-xs text-white/70 leading-relaxed">
                  {caseIdParam
                    ? 'Консилиум по этому делу ещё не собирался: хранилища решений в системе нет, и показать здесь нечего. Соберите его вручную из дела.'
                    : 'Откройте консилиум из карточки дела — тогда у разбора будет предмет. Без дела экран показывать нечего, и выдумывать он не будет.'}
                </p>

                <div className="pt-2">
                  <button
                    onClick={() => setShowCouncilModal(true)}
                    className="px-4 py-2 rounded-lg bg-[#C8A653] text-[#121912] text-xs font-bold hover:bg-[#B8933E] transition-colors cursor-pointer inline-flex items-center gap-2"
                  >
                    <Scale className="w-4 h-4" />
                    Что такое консилиум
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Правый сайдбар: Live сигналы + Срочные дедлайны + Языковые правила */}
        <aside className="space-y-4">
          {/* Live operating signals */}
          <div className="bg-white border border-[#E5E1D6] rounded-xl p-4 shadow-2xs">
            <div className="flex items-center gap-2 text-xs font-bold text-[#20231F]">
              <Radio className="w-4 h-4 text-[#0B8B5E]" />
              Live operating signals
            </div>

            <div className="mt-4 space-y-3">
              {(liveAgents.length > 0
                ? [
                    [
                      liveAgents.find((a) => a.name.toLowerCase().includes('katifi'))?.name || 'Katifi Core',
                      `Orchestrating active practice swarm · ${liveAgents.length} agents`,
                      'amber'
                    ],
                    [
                      liveAgents.find((a) => a.name.toLowerCase().includes('sofia'))?.name || 'Sofia Voice',
                      'Ready 24/7 · English, Russian & Spanish active',
                      'green'
                    ],
                    [
                      liveAgents.find((a) => a.name.toLowerCase().includes('elena'))?.name || 'Elena Paralegal',
                      'Checking USCIS evidence checklists & forms',
                      'blue'
                    ]
                  ]
                : [
                    ['Katifi Core', 'Preparing 2 actions for review', 'amber'],
                    ['Sofia Voice', 'No active calls · 3 languages active', 'green'],
                    ['Elena Paralegal', 'Checking escrow evidence', 'blue']
                  ]
              ).map(([agentName, statusText, colorDot]) => (
                <div key={agentName} className="flex items-center gap-2.5">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      colorDot === 'green'
                        ? 'bg-[#0B8B5E]'
                        : colorDot === 'amber'
                        ? 'bg-[#D78416]'
                        : 'bg-[#3674D8]'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-[#20231F]">
                      {agentName}
                    </p>
                    <p className="text-[10px] text-[#687077] truncate">
                      {statusText}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 🔴 ПАМЯТКА О СРОКЕ УБРАНА. Здесь стояло «I-94 конкретного клиента
              истекает через 18 дней» — выдуманный срок по выдуманному человеку,
              с призывом бросить остальное и открыть дело. Срок — то, по чему
              принимают решения; выдуманный срок опаснее отсутствующего.
              Держателя сроков в системе нет, и заводить его этой задачей не
              будем. Осталась ссылка на дела — она никому ничего не обещает. */}
          <div className="bg-white border border-[#E5E1D6] rounded-xl p-4 shadow-2xs space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold text-[#687077]">
              <AlertTriangle className="w-4 h-4 text-[#687077]" />
              Сроков система не считает
            </div>
            <p className="text-xs text-[#20231F] leading-relaxed">
              Учёта сроков по делам в системе пока нет — и показывать здесь нечего.
              Сроки смотрите в самих делах.
            </p>
            <button
              onClick={handleNavigateCase}
              className="text-xs font-bold text-[#A97F2E] hover:underline cursor-pointer inline-flex items-center gap-1 pt-1"
            >
              Открыть дела →
            </button>
          </div>

          {/* AI Language & Guardrails */}
          <button
            onClick={() => setShowGuardrailsModal(true)}
            className="w-full p-4 bg-white border border-[#E5E1D6] rounded-xl text-left hover:border-[#C8A653] transition-colors cursor-pointer shadow-2xs block"
          >
            <div className="flex items-center gap-2 text-xs font-bold text-[#20231F]">
              <Sliders className="w-4 h-4 text-[#7554D8]" />
              AI language & guardrails
            </div>
            <p className="text-[11px] text-[#687077] mt-1 leading-relaxed">
              Manage voice languages, greeting scripts, roles and Florida Bar human approval rules.
            </p>
          </button>
        </aside>
      </section>

      {/* ========================================================================= */}
      {/* 4. МОДАЛЬНОЕ ОКНО: что такое консилиум                                     */}
      {/* ========================================================================= */}
      {/* 🔴 ЗДЕСЬ СТОЯЛО ПОЛНОЕ ЮРИДИЧЕСКОЕ ЗАКЛЮЧЕНИЕ ПО ВЫДУМАННОМУ ДЕЛУ:
          «Risk 90 / Critical», расхождение двух названных по именам советников,
          решение «подготовить двойной пакет до 15 сентября», кнопка «Принять
          рекомендацию и создать задачу», которая рапортовала об успехе, ничего
          не создав. Ни одного живого источника за этим не было: ни ручки
          консилиума, ни ручки создания задачи. Убрано целиком; осталось
          описание ЗАМЫСЛА, где ни одна фраза не выдаёт себя за состояние дел. */}
      {showCouncilModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-[#121912] border border-[#2D3A2D] text-white rounded-2xl max-w-2xl w-full p-6 space-y-5 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="flex items-start justify-between border-b border-[#2D3A2D] pb-4">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold text-[#C8A653] uppercase tracking-wider">
                  <Scale className="w-4 h-4" />
                  Интеллектуальный иммиграционный консилиум
                </div>
                <h2 className="text-xl font-serif font-normal text-white mt-1">
                  Замысел, а не состояние дел
                </h2>
              </div>
              <button
                onClick={() => setShowCouncilModal(false)}
                className="p-1 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-[#1A241A] p-4 rounded-xl border border-[#2D3A2D] space-y-3 text-xs leading-relaxed text-[#E0E8E0]">
              <p>
                <strong>Что это:</strong> разбор, в котором роли практики (риск-советник,
                paralegal, Katifi) высказываются по одному делу порознь, а расхождение
                между ними выносится адвокату как решение, которое обязан принять человек.
              </p>
              <p>
                <strong>Чего здесь НЕТ:</strong> хранилища решений консилиума и ручки,
                которая их отдаёт. Пока их не будет, экран не покажет ни одного разбора —
                ни настоящего, ни правдоподобного.
              </p>
              <div className="p-3 rounded bg-[#2D1B1B] border border-[#C84842]/40 text-[#F6D0D0]">
                До 15.09.2026 на этом месте стояло готовое заключение по клиенту,
                которого не существует. Отличить его от настоящего было нечем.
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowCouncilModal(false)}
                className="px-4 py-2.5 bg-[#C8A653] hover:bg-[#B8933E] text-[#121912] font-bold text-xs rounded-lg transition-colors cursor-pointer shadow-sm"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. МОДАЛЬНОЕ ОКНО: AI Language & Florida Bar Guardrails                   */}
      {/* ========================================================================= */}
      {showGuardrailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl text-[#20231F] animate-in fade-in zoom-in-95">
            <div className="flex items-start justify-between border-b border-[#E5E1D6] pb-3">
              <div>
                <h3 className="font-serif text-lg font-bold text-[#20231F]">
                  AI Language & Guardrails
                </h3>
                <p className="text-xs text-[#687077] mt-0.5">
                  Florida Bar Rule 4-5.3 & Ethical AI Compliance
                </p>
              </div>
              <button
                onClick={() => setShowGuardrailsModal(false)}
                className="p-1 rounded text-[#687077] hover:text-[#20231F] hover:bg-[#F8F7F2] transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold block text-[#20231F]">
                  Поддерживаемые языки Sofia Voice
                </label>
                <div className="flex flex-wrap gap-2">
                  {['English (US)', 'Russian', 'Spanish (Latin America)'].map(
                    (lang) => (
                      <span
                        key={lang}
                        className="px-2.5 py-1 rounded bg-[#F8F7F2] border border-[#E5E1D6] font-semibold text-[#20231F]"
                      >
                        ✓ {lang}
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold block text-[#20231F]">
                  Anti-UPL (Защита от несанкционированной юридической практики)
                </label>
                <p className="text-[#687077] leading-relaxed">
                  Sofia Voice и Elena Paralegal действуют в режиме сбора фактов. Никакие правовые заключения, соглашения о представительстве или оценки шансов одобрения не выдаются клиенту без прямой авторизации лицензированным адвокатом (Human-in-the-loop).
                </p>
              </div>

              <div className="p-3 rounded-lg bg-[#FAF4E5] border border-[#C8A653]/30 text-[#83692A]">
                <strong>Аудит-след:</strong> Все транскрипты, решения консилиума и драфты документов логируются с фиксацией версии промпта и ответственного юриста.
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setShowGuardrailsModal(false)}
                className="px-4 py-2 bg-[#20231F] text-white font-bold text-xs rounded-lg hover:bg-black transition-colors cursor-pointer"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
